/**
 * User-facing Copilot error copy (mirrors timesheet AI error resolution).
 * Maps AI service codes / HTTP status to safe end-user messages.
 */

export const VERTEX360_COPILOT_ERROR_MESSAGES = {
  SP_AI_DISABLED: "Vertex360 Copilot isn’t enabled for your account.",
  SP_FEATURE_DISABLED: "Vertex360 Copilot isn’t enabled for your organisation.",
  AI_FEATURE_NOT_AUTHORIZED: "You don’t have permission to use Vertex360 Copilot.",
  PERMISSION_DENIED: "You don’t have permission to use Vertex360 Copilot.",
  AI_CREDITS_EXHAUSTED:
    "Insufficient Vertex360 Copilot credits. Please top up your balance and try again.",
  AI_UNAVAILABLE: "Vertex360 Copilot is temporarily unavailable. Please try again shortly.",
  COPILOT_STREAM_ERROR:
    "Vertex360 Copilot is temporarily unavailable. Please try again shortly.",
  NETWORK_ERROR:
    "Vertex360 Copilot is unavailable. Please check your connection and try again.",
  TIMEOUT: "The Vertex360 Copilot request timed out. Please try again.",
  CANCELLED: "Request cancelled.",
};

const INTERNAL_AI_ERROR_HINT =
  /openai|language model|api[_ -]?key|server configuration|development placeholder/i;

/**
 * @param {Error & { status?: number, code?: string }} [error]
 * @param {string} [fallback]
 * @returns {string}
 */
export const resolveVertex360CopilotErrorMessage = (error, fallback = "") => {
  const status = Number(error?.status);
  const code = String(error?.code || "").trim();

  if (code === "CANCELLED") {
    return VERTEX360_COPILOT_ERROR_MESSAGES.CANCELLED;
  }

  if (status === 403 || code === "SP_FEATURE_DISABLED" || code === "SP_AI_DISABLED") {
    if (code && VERTEX360_COPILOT_ERROR_MESSAGES[code]) {
      return VERTEX360_COPILOT_ERROR_MESSAGES[code];
    }
    return VERTEX360_COPILOT_ERROR_MESSAGES.SP_FEATURE_DISABLED;
  }

  if (
    code === "AI_FEATURE_NOT_AUTHORIZED" ||
    code === "PERMISSION_DENIED" ||
    code === "USER_TYPE_NOT_ALLOWED"
  ) {
    return VERTEX360_COPILOT_ERROR_MESSAGES.AI_FEATURE_NOT_AUTHORIZED;
  }

  if (code === "AI_CREDITS_EXHAUSTED" || status === 402) {
    return VERTEX360_COPILOT_ERROR_MESSAGES.AI_CREDITS_EXHAUSTED;
  }

  if (code && VERTEX360_COPILOT_ERROR_MESSAGES[code]) {
    return VERTEX360_COPILOT_ERROR_MESSAGES[code];
  }

  const message = String(error?.message || fallback || "").trim();
  if (message && !INTERNAL_AI_ERROR_HINT.test(message)) {
    if (/something went wrong/i.test(message)) {
      return VERTEX360_COPILOT_ERROR_MESSAGES.AI_UNAVAILABLE;
    }
    if (/not enabled for your organisation/i.test(message)) {
      return VERTEX360_COPILOT_ERROR_MESSAGES.SP_FEATURE_DISABLED;
    }
    if (/do not have permission/i.test(message)) {
      return VERTEX360_COPILOT_ERROR_MESSAGES.AI_FEATURE_NOT_AUTHORIZED;
    }
    return message;
  }

  if (status === 503 || status >= 500) {
    return VERTEX360_COPILOT_ERROR_MESSAGES.AI_UNAVAILABLE;
  }

  if (code === "NETWORK_ERROR" || error?.request) {
    return VERTEX360_COPILOT_ERROR_MESSAGES.NETWORK_ERROR;
  }

  return VERTEX360_COPILOT_ERROR_MESSAGES.AI_UNAVAILABLE;
};
